import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Dialog,Grid,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as PendingIcon,
  Info as InfoIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import axios from 'axios';
import baseUrl from '../config';
import { formatDistanceToNow, format } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';

const StudentQuizDashboard = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [attendingQuiz, setAttendingQuiz] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Get student ID from auth context or localStorage
  const user = JSON.parse(localStorage.getItem('user'));
  const studentId = user ? user.id : null;  // This ensures that we don't get an error if 'user' is null
  console.log(studentId);
  

  useEffect(() => {
    const fetchQuizzes = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}api/students/quiz/${studentId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setQuizzes(response.data.quizResults);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch your quizzes');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizzes();
  }, [studentId]);

  useEffect(() => {
    // Calculate time remaining for pending quizzes
    if (quizzes.length > 0) {
      const timer = setInterval(() => {
        quizzes.forEach(quiz => {
          if (quiz.quizPassed === 'Pending' && quiz.quizLink) {
            const assignedTime = new Date(quiz.linkAssignedAt).getTime();
            const elapsed = Date.now() - assignedTime;
            const remaining = Math.max(0, 7200000 - elapsed); // 2 hours in ms
            setTimeRemaining(remaining);
          }
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [quizzes]);

  const handleMarkAttended = async (quizIndex) => {
    try {
      setAttendingQuiz(true);
      await axios.post(`${baseUrl}api/students/mark-quiz-attended`, {
        studentId,
        quizIndex
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Refresh the quiz list
      const response = await axios.get(`${baseUrl}api/students/quiz/${studentId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setQuizzes(response.data.quizResults);
    } catch (err) {
      setError(err.response?.data?.message || 'Error marking quiz as attended');
    } finally {
      setAttendingQuiz(false);
    }
  };

  const formatTime = (ms) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Passed': return <CheckCircleIcon color="success" />;
      case 'Failed': return <CancelIcon color="error" />;
      default: return <PendingIcon color="warning" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Passed': return 'success';
      case 'Failed': return 'error';
      default: return 'warning';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Your Quiz Assignments
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress size={60} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : quizzes.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No quizzes assigned yet. Check back later!
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {quizzes.map((quiz, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" component="div">
                      Quiz {index + 1}
                    </Typography>
                    <Chip
                      label={quiz.quizPassed}
                      icon={getStatusIcon(quiz.quizPassed)}
                      variant="outlined"
                      color={getStatusColor(quiz.quizPassed)}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Assigned:</strong> {format(new Date(quiz.quizDate), 'MMM d, yyyy h:mm a')}
                  </Typography>

                  {quiz.quizPassed === 'Pending' && quiz.quizLink && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <TimerIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Time remaining: {timeRemaining ? formatTime(timeRemaining) : 'Calculating...'}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={timeRemaining ? (100 - (timeRemaining / 7200000) * 100) : 0} 
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  )}

                  {quiz.quizScore !== null && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Your Score:</strong> {quiz.quizScore}
                    </Typography>
                  )}

                  {quiz.performance && (
                    <Typography variant="body2">
                      <strong>Feedback:</strong> {quiz.performance}
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{
                  justifyContent: 'space-between',
                  p: 2,
                }}>
                  {quiz.quizLink && quiz.quizLink !== 'Link Expired' ? (
                    <Button
                      variant="contained"
                      startIcon={<LinkIcon />}
                      href={quiz.quizLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mr: 1 }}
                    >
                      Take Quiz
                    </Button>
                  ) : (
                    <Tooltip title="Quiz link expired after 2 hours">
                      <Button
                        variant="outlined"
                        disabled
                        startIcon={<InfoIcon />}
                      >
                        Link Expired
                      </Button>
                    </Tooltip>
                  )}

                    <Button
                      variant="outlined"
                      onClick={() => handleMarkAttended(index)}
                      disabled={attendingQuiz}
                    >
                      {attendingQuiz ? 'Marking...' : 'Mark Attended'}
                    </Button>
                  
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Quiz Details Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'white'
        }}>
          <Typography variant="h6">Quiz Details</Typography>
          <IconButton onClick={() => setOpenDialog(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedQuiz && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedQuiz.quizLink || 'No link available'}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={selectedQuiz.quizPassed}
                  icon={getStatusIcon(selectedQuiz.quizPassed)}
                  color={getStatusColor(selectedQuiz.quizPassed)}
                  sx={{ mb: 1 }}
                />
              </Box>

              <Typography variant="body1" gutterBottom>
                <strong>Date:</strong> {selectedQuiz.quizDate}
              </Typography>

              {selectedQuiz.quizScore !== null && (
                <Typography variant="body1" gutterBottom>
                  <strong>Score:</strong> {selectedQuiz.quizScore}
                </Typography>
              )}

              {selectedQuiz.performance && (
                <Typography variant="body1" gutterBottom>
                  <strong>Feedback:</strong> {selectedQuiz.performance}
                </Typography>
              )}

              {selectedQuiz.quizPassed === 'Pending' && selectedQuiz.quizLink && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    You have {timeRemaining ? formatTime(timeRemaining) : 'limited time'} to complete this quiz
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentQuizDashboard;